import { useContext, useEffect } from "react";
import {
  Modal,
  ModalBody,
  Badge,
  DropdownItem,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  Alert,
} from "reactstrap";
import { isEmpty } from "lodash";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import Icon from "../../../components/Icon/Icon";
import PaginationComponent from "./../../../components/Pagination/Pagination";
import TableFilter from "../../../components/TableFilter/TableFilter";
import PageHeader from "../../../components/PageHeader/PageHeader";
import SearchTable from "../../../components/SearchTable/SearchTable";
import InfoItem from "./../../../helpers/InfoItem/InfoItem";
import UserAvatar from "./../../../components/UserAvatar/UserAvatar";
import SpecialIcon from "../../../helpers/SpecialIcon/SpecialIcon";
import UserInfoItem from "./../../../components/UserInfoItem/UserInfoItem";
import Currency, { ConvertCurrency } from "../../../helpers/Currency/Currency";
import { Block } from "./../../../components/Block/Block";
import {
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableItem,
  DataTableRow,
} from "./../../../components/DataTable/DataTable";
import { Col, Row } from "./../../../components/Grid/Grid";
import { convertDate, numberSeparate } from "../../../utils";
import {
  convertOrderStatusToColor,
  convertOrderStatusToText,
} from "../../../utils/enum";
import { orderAdminContext } from "../../../context/AdminContext/OrderAdminContext/OrderAdminContext";

const OrdersAdmin = () => {
  const permissions = useSelector((state) => state.permissions);

  const { pathname } = useLocation();

  const {
    orders,
    order,
    setOrder,
    accounting,
    filter,
    setFilter,
    sort,
    setSort,
    start,
    setStart,
    end,
    setEnd,
    status,
    setStatus,
    pageCount,
    itemsCount,
    setItemsCount,
    pageNumber,
    setPageNumber,
    handleGetOrders,
  } = useContext(orderAdminContext);

  useEffect(() => {
    handleGetOrders();
  }, [start, end, status, pageNumber, itemsCount, sort, pathname]);

  const isAccounting = pathname.includes("/accounting");

  return (
    <>
      <SiteTitle title={isAccounting ? "حسابداری" : "سفارشات"} />

      <PageHeader title={isAccounting ? "حسابداری" : "سفارشات"} />

      <TableFilter
        setItemsCount={setItemsCount}
        itemsCount={itemsCount}
        setSort={setSort}
        sort={sort}
        setStart={setStart}
        start={start}
        setEnd={setEnd}
        end={end}
        setOrderStatus={setStatus}
        orderStatus={status}
      />

      <Block>
        <DataTable className="card-stretch">
          <SearchTable
            filterPlaceholder="نام، کد پیگیری و ..."
            filter={filter}
            setFilter={setFilter}
            onSearch={handleGetOrders}
          />
          <DataTableBody>
            <DataTableHead>
              <DataTableRow>
                <span className="sub-text">#</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">مشتری</span>
              </DataTableRow>
              <DataTableRow size="sm">
                <span className="sub-text">قیمت کل</span>
              </DataTableRow>
              <DataTableRow size="lg">
                <span className="sub-text">کد پیگیری</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">وضعیت</span>
              </DataTableRow>
              <DataTableRow size="xl">
                <span className="sub-text">شروع</span>
              </DataTableRow>
              <DataTableRow size="xl">
                <span className="sub-text">پایان</span>
              </DataTableRow>
              <DataTableRow />
            </DataTableHead>
            {/*Head*/}
            {orders.length > 0
              ? orders.map((item, index) => {
                  return (
                    <DataTableItem key={item._id}>
                      <DataTableRow>
                        <span>{index + 1}</span>
                      </DataTableRow>
                      <DataTableRow>
                        <div className="user-card">
                          <UserAvatar
                            {...(item?.user?.avatar
                              ? { image: item?.user?.avatar }
                              : {
                                  icon: "user-alt",
                                })}
                          />
                          <UserInfoItem
                            title={`${item?.user?.firstName} ${item?.user?.lastName}`}
                            subTitle={item?.user?.phoneNumber}
                          />
                        </div>
                      </DataTableRow>
                      <DataTableRow size="sm">
                        <ConvertCurrency price={item?.pricing?.totalAmount} />{" "}
                        <Currency />
                      </DataTableRow>
                      <DataTableRow size="lg">
                        <span>{item.trackingCode}</span>
                      </DataTableRow>
                      <DataTableRow>
                        <Badge
                          className="badge-dim"
                          color={convertOrderStatusToColor(item?.status)}
                        >
                          {convertOrderStatusToText(item?.status)}
                        </Badge>
                      </DataTableRow>
                      <DataTableRow size="xl">
                        <span>
                          {convertDate(item.startDate, "YYYY/MM/DD - HH:mm")}
                        </span>
                      </DataTableRow>
                      <DataTableRow size="xl">
                        <span>
                          {convertDate(item.endDate, "YYYY/MM/DD - HH:mm")}
                        </span>
                      </DataTableRow>

                      <DataTableRow className="nk-tb-col-tools">
                        <ul className="nk-tb-actions gx-1">
                          <li>
                            <UncontrolledDropdown>
                              <DropdownToggle
                                tag="a"
                                className="dropdown-toggle btn btn-icon btn-trigger"
                              >
                                <Icon name="more-h" />
                              </DropdownToggle>
                              <DropdownMenu left>
                                <ul className="link-list-opt no-bdr text-right">
                                  <li
                                    onClick={() => {
                                      setOrder(item);
                                    }}
                                  >
                                    <DropdownItem tag="a" type="button">
                                      <Icon name="eye" />
                                      <span>جزئیات</span>
                                    </DropdownItem>
                                  </li>
                                </ul>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </li>
                        </ul>
                      </DataTableRow>
                    </DataTableItem>
                  );
                })
              : null}
          </DataTableBody>
          {isAccounting && !isEmpty(accounting) && (
            <div className="card-inner">
              <Row className="gy-4">
                <Col size="12" sm="6" lg="4">
                  <InfoItem title="جمع کسر کیف پول">
                    <ConvertCurrency price={accounting?.totalDeductWallet} />{" "}
                    <Currency />
                  </InfoItem>
                </Col>
                {permissions.canProfit && (
                  <Col size="12" sm="6" lg="4">
                    <InfoItem title="جمع سود خالص محصولات">
                      <ConvertCurrency price={accounting?.totalProductProfit} />{" "}
                      <Currency />
                    </InfoItem>
                  </Col>
                )}
                <Col size="12" sm="6" lg="4">
                  <InfoItem title="جمع مبلغ نهایی">
                    <ConvertCurrency price={accounting?.totalTotalAmount} />{" "}
                    <Currency />
                  </InfoItem>
                </Col>
                <Col size="12" sm="6" lg="4">
                  <InfoItem title="جمع مبلغ پرداختی کاربر">
                    <ConvertCurrency
                      price={
                        accounting?.totalTotalAmount -
                        accounting?.totalDeductWallet
                      }
                    />{" "}
                    <Currency />
                  </InfoItem>
                </Col>
                <Col size="12" sm="6" lg="4">
                  <InfoItem title="جمع مبلغ محصولات">
                    <ConvertCurrency
                      price={accounting?.totalTotalProductAmount}
                    />{" "}
                    <Currency />
                  </InfoItem>
                </Col>
                <Col size="12" sm="6" lg="4">
                  <InfoItem title="جمع مبلغ میزها">
                    <ConvertCurrency
                      price={accounting?.totalTotalTableAmount}
                    />{" "}
                    <Currency />
                  </InfoItem>
                </Col>
              </Row>
            </div>
          )}
          <div className="card-inner">
            {orders.length > 0 ? (
              <PaginationComponent
                pageCount={pageCount}
                currentPage={pageNumber}
                setPageNumber={setPageNumber}
              />
            ) : (
              <div className="text-center">
                <span className="text-silent">داده ای برای نمایش یافت نشد</span>
              </div>
            )}
          </div>
        </DataTable>
      </Block>
      <Modal
        isOpen={!isEmpty(order)}
        toggle={() => setOrder({})}
        className="modal-dialog-centered"
        size="md"
      >
        <ModalBody>
          <span type="button" onClick={() => setOrder({})} className="close">
            <Icon name="cross-sm" />
          </span>
          <div className="p-2">
            <h5 className="title">جزئیات سفارش </h5>
            <br />
            <span className="text-primary border-bottom fw-bold">
              اطلاعات مشتری
            </span>
            <Row>
              <Col sm="6">
                <InfoItem title="نام">
                  {order.user?.firstName} {order.user?.isVIP && <SpecialIcon />}
                </InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="نام خانوادگی">{order.user?.lastName}</InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="شماره تماس">
                  {order?.user?.phoneNumber}
                </InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="تاریخ تولد">
                  {convertDate(order?.user?.birthDate)}
                </InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="موجودی">
                  <ConvertCurrency price={order?.user?.wallet} /> <Currency />
                </InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="وضعیت کاربر">
                  <Badge
                    className="badge-dim has-bg"
                    color={order?.user?.isActive ? "success" : "danger"}
                  >
                    {order?.user?.isActive ? "فعال" : "غیرفعال"}
                  </Badge>
                </InfoItem>
              </Col>
            </Row>
            <br />
            {!isEmpty(order.table) && (
              <>
                <span className="text-primary border-bottom fw-bold">
                  اطلاعات میز
                </span>
                <Row>
                  <Col sm="6">
                    <InfoItem title="نام">
                      {order.table?.title}{" "}
                      {order.user?.isVIP && <SpecialIcon />}
                    </InfoItem>
                  </Col>
                  <Col sm="6">
                    <InfoItem title="قیمت">
                      <ConvertCurrency price={order?.table?.price} />{" "}
                      <Currency />
                    </InfoItem>
                  </Col>
                  <Col sm="6">
                    <InfoItem title="چوب اضافه">
                      {numberSeparate(order.table?.cue?.count)}
                    </InfoItem>
                  </Col>
                </Row>
                <br />
              </>
            )}
            <span className="text-primary border-bottom fw-bold">
              اطلاعات سفارش
            </span>
            <Row>
              <Col sm="6">
                <InfoItem title="شروع">
                  {convertDate(order.startDate, "YYYY/MM/DD - HH:mm")}
                </InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="پایان">
                  {convertDate(order.endDate, "YYYY/MM/DD - HH:mm")}
                </InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="کد پیگیری">{order.trackingCode}</InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="وضعیت سفارش">
                  <Badge
                    className="badge-dim"
                    color={convertOrderStatusToColor(order?.status)}
                  >
                    {convertOrderStatusToText(order?.status)}
                  </Badge>
                </InfoItem>
              </Col>
              <Col size="12">
                <InfoItem title="توضیحات">{order.description}</InfoItem>
              </Col>
            </Row>
            <br />
            <span className="text-primary border-bottom fw-bold">
              اطلاعات پرداخت
            </span>
            <Row>
              <Col sm="6">
                <InfoItem title="مبلغ کسر شده از کیف پول کاربر">
                  <ConvertCurrency price={order?.pricing?.deductWallet} />
                </InfoItem>
              </Col>
              {permissions.canProfit && (
                <Col sm="6">
                  <InfoItem title="سود خالص محصولات">
                    <ConvertCurrency price={order?.pricing?.productProfit} />
                  </InfoItem>
                </Col>
              )}
              <Col sm="6">
                <InfoItem title="مبلغ نهایی محصولات">
                  <ConvertCurrency price={order?.pricing?.totalProductAmount} />
                </InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="مبلغ نهایی میز">
                  <ConvertCurrency price={order?.pricing?.totalTableAmount} />
                </InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="مبلغ نهایی">
                  <ConvertCurrency price={order?.pricing?.totalAmount} />
                </InfoItem>
              </Col>
              <Col sm="12" className="mt-3">
                <Alert color="primary" className="text-center">
                  <InfoItem title="مبلغ نهایی پرداختی کاربر">
                    <ConvertCurrency
                      price={
                        order?.pricing?.totalAmount -
                        order?.pricing?.deductWallet
                      }
                    />{" "}
                    <Currency />
                  </InfoItem>
                </Alert>
              </Col>
            </Row>
            <br />

            {!isEmpty(order?.products) && (
              <>
                <span className="text-primary border-bottom fw-bold">
                  اطلاعات {order?.products?.length} محصول
                </span>
                {order?.products?.map((item) => (
                  <Row
                    key={item?._id}
                    className="border rounded mb-2 py-1 border-primary position-relative m-0"
                  >
                    <Col size="12" className="mb-2">
                      <h5 className="mt-1">{item?.title}</h5>
                    </Col>
                    <Col sm="4">
                      <InfoItem title="تعداد">
                        <Badge color="primary">{item?.count}</Badge>
                      </InfoItem>
                    </Col>
                    <Col sm="4">
                      <InfoItem title="قیمت">
                        <ConvertCurrency price={item.price} /> <Currency />
                      </InfoItem>
                    </Col>
                    <Col sm="4">
                      <InfoItem title="مبلغ کل">
                        <ConvertCurrency price={item.price * item?.count} />{" "}
                        <Currency />
                      </InfoItem>
                    </Col>
                  </Row>
                ))}
              </>
            )}
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default OrdersAdmin;
